import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { SudokuBoard } from './SudokuBoard';
import { generateSudokuPuzzle } from '../../lib/generateSudokuPuzzle';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useSound } from '../../hooks/useSound';
import { getDateSeed } from '../../lib/dateSeed';
import {
  createTimerTick,
  getIncompleteDailyGames,
  saveGameToDatabase,
  checkPersonalBest
} from '../../lib/gameUtils';
import { debug } from '../../lib/utils';
import { useAuth } from '../../hooks/useAuth';
import {
  SudokuGameHeader,
  SudokuInstructions,
  SudokuResetConfirmationDialog
} from './SudokuGameView';
import { SudokuCompletionDialog } from './SudokuCompletionDialog';
import type { SudokuGameStats } from './sudokuCompletion';
import {
  canRestorePuzzleState,
  createDailyPuzzleIdentity,
  formatDateString as formatPuzzleDateString,
  parseDateString,
  type DailyPuzzleGame,
  type DailyPuzzleIdentity
} from '../../lib/dailyPuzzle';
import { useDailyPuzzleRollover } from '../../hooks/useDailyPuzzleRollover';

// Move utility functions outside the component
const isPuzzleComplete = (grid: number[][], solution: number[][]) => {
  return grid.every((row, i) => row.every((cell, j) => cell === solution[i][j]));
};

// Function to create empty 9x9 grid
const createEmptyGrid = () => Array(9).fill(null).map(() => Array(9).fill(0));

// Default game statistics
const DEFAULT_GAME_STATS: SudokuGameStats = {
  errors: 0,
  pencilErrors: 0,
  hintsUsed: 0,
  coverageUsed: false,
  pencilMarksEntered: false,
  autoPencilUsed: false
};

const SUDOKU_IDENTITY_KEY = 'sudokuPuzzleIdentity';

const readSavedSudokuSeed = (): number | undefined => {
  const savedSeed = localStorage.getItem('sudokuSeed');
  if (savedSeed === null) {
    return undefined;
  }

  const parsedSeed = Number(savedSeed);
  if (!Number.isSafeInteger(parsedSeed)) {
    throw new Error(`Invalid saved Sudoku seed: ${savedSeed}`);
  }

  return parsedSeed;
};

interface SudokuGameProps {
  initialSeed?: number;
  puzzleDate?: Date | null;
  difficulty?: number | null;
  isPreviewMode?: boolean;
}

// Add this function to handle user preferred difficulty
const getUserPreferredDifficulty = (): number => {
  const storedDifficulty = localStorage.getItem('sudokuPreferredDifficulty');
  if (storedDifficulty) {
    const difficulty = parseInt(storedDifficulty, 10);
    if ([1, 2, 3, 4].includes(difficulty)) {
      return difficulty;
    }
  }
  return 3; // Default to 'hard' if no valid preference is found
};

// Add this function to save user preferred difficulty
const saveUserPreferredDifficulty = (difficulty: number): void => {
  localStorage.setItem('sudokuPreferredDifficulty', difficulty.toString());
};

export function SudokuGame({ initialSeed, puzzleDate, difficulty: initialDifficulty, isPreviewMode = false }: SudokuGameProps) {
  const isInitialized = useRef(false);
  const initialSeedApplied = useRef(false);

  // Use the provided difficulty or get from user preferences
  const [difficulty, setDifficulty] = useState<number>(
    () => initialDifficulty ?? getUserPreferredDifficulty()
  );
  const initialPuzzleIdentity = useRef<DailyPuzzleIdentity>(
    createDailyPuzzleIdentity(
      'sudoku',
      puzzleDate || new Date(),
      initialDifficulty ?? getUserPreferredDifficulty(),
      initialSeed
    )
  );
  const canRestoreInitialState = useRef(
    canRestorePuzzleState(
      localStorage.getItem(SUDOKU_IDENTITY_KEY),
      readSavedSudokuSeed(),
      initialPuzzleIdentity.current
    )
  );

  // Add state for current puzzle date
  const [currentPuzzleDate, setCurrentPuzzleDate] = useState<Date>(
    () => parseDateString(initialPuzzleIdentity.current.date)
  );

  // Initialize all state variables first
  const [grid, setGrid] = useState(createEmptyGrid);
  const [originalGrid, setOriginalGrid] = useState(createEmptyGrid);
  const [solution, setSolution] = useState(createEmptyGrid);
  const [pencilMarks, setPencilMarks] = useState<Record<string, number[]>>({});
  const [hintedCell, setHintedCell] = useState<{row: number, col: number} | null>(null);
  const [showResetGameConfirmation, setShowResetGameConfirmation] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(() => {
    if (!canRestoreInitialState.current) {
      return 0;
    }
    const savedTime = localStorage.getItem('sudokuElapsedTime');
    return savedTime ? parseInt(savedTime) : 0;
  });

  const [currentSeed, setCurrentSeed] = useState<number | undefined>(undefined);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPuzzleCompleted, setIsPuzzleCompleted] = useState(() => {
    return canRestoreInitialState.current && localStorage.getItem('sudokuCompleted') === 'true';
  });
  const [gameStats, setGameStats] = useState(DEFAULT_GAME_STATS);
  const [highlightNumber, setHighlightNumber] = useState<number | null>(() => {
    if (!canRestoreInitialState.current) {
      return null;
    }
    const savedHighlight = localStorage.getItem('sudokuHighlightNumber');
    return savedHighlight ? parseInt(savedHighlight, 10) : null;
  });

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const boardRef = useRef<{ resetSelection: () => void; setCompletionProcessing: (value: boolean) => void }>(null);
  const resetGameRef = useRef<(skipConfirmation?: boolean) => boolean>(() => true);

  const { t } = useTranslation();
  const { currentUser } = useAuth();

  // Add sound hooks
  const { play: playStepCompleteSound } = useSound('/assets/sounds/step-complete.mp3');
  const { play: playPuzzleCompleteSound } = useSound('/assets/sounds/puzzle-complete.mp3');
  const { play: playErrorSound } = useSound('/assets/sounds/error.mp3');
  const { play: playTurnOnSound } = useSound('/assets/sounds/turn-on.mp3');
  const { play: playTurnOffSound } = useSound('/assets/sounds/turn-off.mp3');
  const { play: playWritePenSound } = useSound('/assets/sounds/write-pen.mp3', { playbackRate: 1.5 });
  const { play: playWritePencilSound } = useSound('/assets/sounds/write-pencil.mp3', { playbackRate: 1.5 });

  // Add state to track what type of confirmation we're showing
  const [confirmationType, setConfirmationType] = useState<'reset' | 'difficulty'>('reset');

  // Add the isDialogOpen state inside the component
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Add state for toggling keyboard controls visibility
  const [showKeyboardControls, setShowKeyboardControls] = useState(false);
  const hasOpenDialog = [
    isDialogOpen,
    showResetGameConfirmation,
    showCompletionDialog
  ].includes(true);

  // Generate and setup a new puzzle
  const setupNewPuzzle = useCallback((identity: DailyPuzzleIdentity) => {
    try {
      debug(`setting up new puzzle with seed: ${identity.seed} and difficulty: ${identity.difficulty}`);
      const { puzzle, solution: newSolution } = generateSudokuPuzzle(identity.seed, identity.difficulty);

      // Update state
      setGrid(puzzle);
      setOriginalGrid(puzzle);
      setSolution(newSolution);
      setPencilMarks({});
      setCurrentSeed(identity.seed);
      setElapsedTime(0);
      setIsPaused(false);
      setIsPuzzleCompleted(false);
      setShowCompletionDialog(false);
      setGameStats(DEFAULT_GAME_STATS);
      setHintedCell(null);
      // Set initial highlight number to center cell value if it exists
      const initialHighlight = puzzle[4][4] !== 0 ? puzzle[4][4] : null;
      setHighlightNumber(initialHighlight);
      localStorage.setItem('sudokuHighlightNumber', initialHighlight?.toString() || '');

      setCurrentPuzzleDate(parseDateString(identity.date));

      // Save to localStorage
      localStorage.setItem('sudokuGrid', JSON.stringify(puzzle));
      localStorage.setItem('sudokuOriginalGrid', JSON.stringify(puzzle));
      localStorage.setItem('sudokuSolution', JSON.stringify(newSolution));
      localStorage.setItem('sudokuSeed', identity.seed.toString());
      localStorage.setItem(SUDOKU_IDENTITY_KEY, JSON.stringify(identity));
      localStorage.setItem('sudokuPencilMarks', JSON.stringify({}));
      localStorage.setItem('sudokuElapsedTime', '0');
      localStorage.removeItem('sudokuCompleted');

      return true;
    } catch (err) {
      console.error('Error generating puzzle:', err);
      return false;
    }
  }, []);

  // Effect for handling seed changes
  useEffect(() => {
    if (!isInitialized.current) {
      // First initialization
      const savedGrid = localStorage.getItem('sudokuGrid');
      const savedSeed = readSavedSudokuSeed();
      const expectedIdentity = initialPuzzleIdentity.current;

      // Generate a new puzzle if needed
      if (!savedGrid || !canRestoreInitialState.current) {
        setupNewPuzzle(expectedIdentity);
        if (initialSeed !== undefined) {
          initialSeedApplied.current = true;
        }
      } else {
        // Load saved game state
        const savedOriginalGrid = localStorage.getItem('sudokuOriginalGrid');
        const savedSolution = localStorage.getItem('sudokuSolution');
        const savedPencilMarks = localStorage.getItem('sudokuPencilMarks');
        const completedStatus = localStorage.getItem('sudokuCompleted') === 'true';

        setGrid(JSON.parse(savedGrid));
        setOriginalGrid(JSON.parse(savedOriginalGrid || savedGrid));
        setSolution(JSON.parse(savedSolution || savedGrid));
        setPencilMarks(savedPencilMarks ? JSON.parse(savedPencilMarks) : {});
        setCurrentSeed(savedSeed);
        setIsPuzzleCompleted(completedStatus);
        setIsPaused(completedStatus);
        setCurrentPuzzleDate(parseDateString(expectedIdentity.date));
        localStorage.setItem(SUDOKU_IDENTITY_KEY, JSON.stringify(expectedIdentity));
      }

      isInitialized.current = true;
    } else if (initialSeed !== undefined && initialSeed !== currentSeed && !initialSeedApplied.current) {
      // Only apply initialSeed if it hasn't been applied before
      setupNewPuzzle(createDailyPuzzleIdentity(
        'sudoku',
        puzzleDate || new Date(),
        difficulty,
        initialSeed
      ));
      initialSeedApplied.current = true;
    }
  }, [initialSeed, currentSeed, setupNewPuzzle, isPuzzleCompleted, difficulty, puzzleDate]);

  const handleDailyDateCheck = useCallback((now: Date) => {
    if (formatPuzzleDateString(now) === formatPuzzleDateString(currentPuzzleDate)) {
      return;
    }

    setupNewPuzzle(createDailyPuzzleIdentity('sudoku', now, difficulty));
  }, [currentPuzzleDate, difficulty, setupNewPuzzle]);

  useDailyPuzzleRollover(initialSeed === undefined && !isPreviewMode, handleDailyDateCheck);

  // Reset game function
  const resetGame = useCallback((skipConfirmation = false) => {
    // Check if confirmation is needed
    if (!skipConfirmation) {
      const hasProgress = grid.some((row, i) =>
        row.some((cell, j) => cell !== originalGrid[i][j])
      );

      if (hasProgress && !isPuzzleComplete(grid, solution)) {
        return false; // Indicate confirmation needed
      }
    }

    // Reset game statistics
    setGameStats(DEFAULT_GAME_STATS);

    // Reset puzzle completion flag
    setIsPuzzleCompleted(false);
    localStorage.removeItem('sudokuCompleted');

    const seed = (initialSeed !== undefined)
      ? initialSeed
      : getDateSeed(currentPuzzleDate, difficulty);
    const identity = createDailyPuzzleIdentity(
      'sudoku',
      currentPuzzleDate,
      difficulty,
      seed
    );

    // Store the current seed
    setCurrentSeed(seed);
    localStorage.setItem('sudokuSeed', seed.toString());
    localStorage.setItem(SUDOKU_IDENTITY_KEY, JSON.stringify(identity));

    const { puzzle, solution: newSolution } = generateSudokuPuzzle(seed, difficulty);
    setGrid(puzzle);
    setOriginalGrid(puzzle);
    setSolution(newSolution);
    setPencilMarks({});
    setHintedCell(null);

    // Reset the board selection
    boardRef.current?.resetSelection();

    // Reset timer
    setElapsedTime(0);
    localStorage.setItem('sudokuElapsedTime', '0');

    // Force unpause state
    setIsPaused(false);

    localStorage.setItem('sudokuGrid', JSON.stringify(puzzle));
    localStorage.setItem('sudokuOriginalGrid', JSON.stringify(puzzle));
    localStorage.setItem('sudokuSolution', JSON.stringify(newSolution));
    localStorage.setItem('sudokuPencilMarks', JSON.stringify({}));

    return true;
  }, [grid, originalGrid, solution, initialSeed, difficulty, currentPuzzleDate]);

  // Update the ref after resetGame is defined
  useEffect(() => {
    resetGameRef.current = resetGame;
  }, [resetGame]);

  // Timer management effect
  useEffect(() => {
    // Always clean up existing timer first
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Start a new timer if not paused, not completed, and not in preview mode
    if (!isPaused && !isPuzzleCompleted && !isPreviewMode) {
      timerRef.current = setInterval(createTimerTick(setElapsedTime), 1000);
    }

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPaused, isPuzzleCompleted, isPreviewMode]);

  // Reset elapsed time if in preview mode
  useEffect(() => {
    if (isPreviewMode) {
      setElapsedTime(0);
    }
  }, [isPreviewMode]);

  // Visibility change handler to auto-pause when tab loses focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !isPaused && !isPuzzleCompleted) {
        setIsPaused(true);

        // Manually clear the timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPaused, isPuzzleCompleted]);

  const handleCellChange = (row: number, col: number, value: number) => {
    // Skip validation with isValidSudokuMove to allow any number
    // Only prevent changing original cells
    if (originalGrid[row][col] === 0) {
      const newGrid = grid.map((r: number[], i: number) =>
        i === row ? r.map((c: number, j: number) => (j === col ? value : c)) : r
      );
      setGrid(newGrid);
      localStorage.setItem('sudokuGrid', JSON.stringify(newGrid));

      // Track errors and show toast
      if (value !== 0 && value !== solution[row][col]) {
        // Play error sound
        playErrorSound();

        const newErrorCount = gameStats.errors + 1;
        setGameStats(prev => ({
          ...prev,
          errors: newErrorCount
        }));

        toast.error(
          newErrorCount === 1
            ? t('toastIncorrectNumber')
            : t('toastIncorrectNumberCount', { count: newErrorCount }),
          { duration: 2000, position: 'top-center' }
        );
      } else if (value !== 0) {
        // Only play write pen sound if we're not completing the puzzle
        // The section/puzzle complete sound will be handled by the board component
        if (!isPuzzleComplete(newGrid, solution) && !isSectionComplete(newGrid, solution, row, col)) {
          playWritePenSound();
        }
      }

      // Check if puzzle is complete
    if (isPuzzleComplete(newGrid, solution)) {
      handleGameComplete();
    }
    }
  };

  // Helper function to check if a section is complete
  const isSectionComplete = (grid: number[][], solution: number[][], row: number, col: number): boolean => {
    // Check row
    let isRowComplete = true;
    for (let c = 0; c < 9; c++) {
      if (grid[row][c] !== solution[row][c]) {
        isRowComplete = false;
        break;
      }
    }
    if (isRowComplete) return true;

    // Check column
    let isColComplete = true;
    for (let r = 0; r < 9; r++) {
      if (grid[r][col] !== solution[r][col]) {
        isColComplete = false;
        break;
      }
    }
    if (isColComplete) return true;

    // Check box
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    let isBoxComplete = true;
    for (let r = boxRow; r < boxRow + 3; r++) {
      for (let c = boxCol; c < boxCol + 3; c++) {
        if (grid[r][c] !== solution[r][c]) {
          isBoxComplete = false;
          break;
        }
      }
      if (!isBoxComplete) break;
    }

    return isBoxComplete;
  };

  const handlePencilMarksChange = (newMarks: Record<string, number[]>, isManualEntry = false, isError = false, isRemoving = false) => {
    setPencilMarks(newMarks);
    localStorage.setItem('sudokuPencilMarks', JSON.stringify(newMarks));

    // Play write pencil sound when manually entering pencil marks (but not for errors or removals)
    if (isManualEntry && !isError && !isRemoving) {
      playWritePencilSound();

      // Track if pencil marks were manually entered (only first time)
      if (!gameStats.pencilMarksEntered && Object.keys(newMarks).length > 0) {
        setGameStats(prev => ({
          ...prev,
          pencilMarksEntered: true
        }));
        toast.info(t('toastPencilMarksEntered'), {
          duration: 2000,
          position: 'top-center'
        });
      }
    }
  };

  const handleHint = () => {
    // Find all empty cells
    const emptyCells = grid.reduce((acc, row, i) => {
      row.forEach((cell, j) => {
        if (cell === 0) {
          acc.push({ row: i, col: j });
        }
      });
      return acc;
    }, [] as { row: number; col: number }[]);

    if (emptyCells.length === 0) return;

    // Track hint usage
    const newHintCount = gameStats.hintsUsed + 1;
    setGameStats(prev => ({
      ...prev,
      hintsUsed: newHintCount
    }));

    toast.info(
      newHintCount === 1
        ? t('toastHintUsed')
        : t('toastHintUsedCount', { count: newHintCount }),
      { duration: 2000, position: 'top-center' }
    );

    // Play write pen sound for hint
    playWritePenSound();

    // Pick a random empty cell
    const randomCell = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    const { row, col } = randomCell;

    // Fill it with the solution value
    const newGrid = grid.map((r, i) =>
      i === row ? r.map((c, j) => (j === col ? solution[row][col] : c)) : r
    );
    setGrid(newGrid);
    localStorage.setItem('sudokuGrid', JSON.stringify(newGrid));

    // Set the hinted cell
    setHintedCell(randomCell);
    setTimeout(() => setHintedCell(null), 1500);

    // Check if puzzle is complete after hint
    if (isPuzzleComplete(newGrid, solution)) {
      handleGameComplete({
        ...gameStats,
        hintsUsed: newHintCount
      });
    }
  };

  // Feature usage tracking handlers
  const handleCoverageUsed = () => {
    if (!gameStats.coverageUsed) {
      // Play turn on sound when coverage is first used
      playTurnOnSound();

      setGameStats(prev => ({
        ...prev,
        coverageUsed: true
      }));
      toast.info(t('toastCoverageUsed'), {
        duration: 2000,
        position: 'top-center'
      });
    }
  };

  const handleAutoPencilUsed = () => {
    if (!gameStats.autoPencilUsed) {
      // Play write pencil sound when auto-pencil is used
      playWritePencilSound();

      setGameStats(prev => ({
        ...prev,
        autoPencilUsed: true
      }));
      toast.info(t('toastAutoPencilUsed'), {
        duration: 2000,
        position: 'top-center'
      });
    }
  };

  const handlePencilError = () => {
    // Play error sound for pencil errors
    playErrorSound();

    const newPencilErrorCount = gameStats.pencilErrors + 1;
    setGameStats(prev => ({
      ...prev,
      pencilErrors: newPencilErrorCount
    }));

    toast.warning(
      newPencilErrorCount === 1
        ? t('toastInvalidPencilMark')
        : t('toastInvalidPencilMarkCount', { count: newPencilErrorCount }),
      { duration: 2000, position: 'top-center' }
    );
  };

  const handleGameComplete = (completedStats: SudokuGameStats = gameStats) => {
    // Stop the timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Set the completion flag
    setIsPuzzleCompleted(true);
    localStorage.setItem('sudokuCompleted', 'true');

    // Save completion time
    const finalTime = elapsedTime;
    localStorage.setItem('sudokuCompletionTime', finalTime.toString());

    // Play completion sound
    playPuzzleCompleteSound();

    // Delay showing the completion dialog to allow grid animation to be visible
    setTimeout(() => {
      // Show completion dialog
      setShowCompletionDialog(true);

      // Save game state to database
      handleSaveGameToDatabase(finalTime, completedStats);
    }, 1500);
  };

  const handleSaveGameToDatabase = async (
    finalTime: number,
    completedStats: SudokuGameStats
  ) => {
    try {
      // Use a default difficulty value if difficulty is null or undefined
      const difficultyValue = difficulty ?? 3;

      // Determine if this was a perfect solve (no mistakes, no hints)
      const isPerfectSolve = completedStats.errors === 0 && completedStats.hintsUsed === 0;

      // Prepare game stats data
      const gameStatsData = {
        ...completedStats,
        perfect_solve: isPerfectSolve
      };

      // Save game data to database
      const { success } = await saveGameToDatabase(
        'sudoku',
        currentPuzzleDate,
        difficultyValue,
        finalTime,
        gameStatsData,
        currentUser
      );

      if (success) {
        // Check if this is a personal best time
        if (isPerfectSolve) {
          const isPersonalBest = await checkPersonalBest('sudoku', finalTime, isPerfectSolve, difficultyValue, currentUser);
          if (isPersonalBest) {
            toast.success(t('toastNewPersonalBest'), {
              position: 'top-center',
              duration: 3000
            });
          }
        }
      }
    } catch (err) {
      console.error('Error in handleSaveGameToDatabase:', err);
    }
  };

  const handleResetGameClick = () => {
    // Check if there's progress before resetting
    const hasProgress = grid.some((row, i) =>
      row.some((cell, j) => cell !== originalGrid[i][j])
    ) || Object.keys(pencilMarks).length > 0;

    if (hasProgress && !isPuzzleComplete(grid, solution)) {
      setConfirmationType('reset');
      setShowResetGameConfirmation(true);
    } else {
      resetCurrentGame();
    }
  };

  const resetCurrentGame = () => {
    // Reset the grid to original state
    setGrid([...originalGrid.map(row => [...row])]);

    // Clear pencil marks
    setPencilMarks({});

    // Reset timer
    setElapsedTime(0);
    localStorage.setItem('sudokuElapsedTime', '0');

    // Reset game stats
    setGameStats(DEFAULT_GAME_STATS);

    // Reset completion status
    setIsPuzzleCompleted(false);
    localStorage.removeItem('sudokuCompleted');

    // Reset hinted cell
    setHintedCell(null);

    // Reset highlight number
    setHighlightNumber(originalGrid[4][4] !== 0 ? originalGrid[4][4] : null);
    localStorage.setItem('sudokuHighlightNumber', originalGrid[4][4]?.toString() || '');

    // Force unpause state
    setIsPaused(false);

    // Reset board selection
    boardRef.current?.resetSelection();

    // Save reset state to localStorage
    localStorage.setItem('sudokuGrid', JSON.stringify(originalGrid));
    localStorage.setItem('sudokuPencilMarks', JSON.stringify({}));
  };

  // Function to handle difficulty change
  const handleDifficultyChange = (newDifficulty: number) => {
    // Do nothing if same difficulty
    if (newDifficulty === difficulty) {
      return false;
    }

    // Check for progress
    const hasProgress = grid.some((row, i) =>
      row.some((cell, j) => cell !== originalGrid[i][j])
    ) || Object.keys(pencilMarks).length > 0;

    if (hasProgress && !isPuzzleComplete(grid, solution)) {
      // Show custom confirmation dialog for difficulty change
      setConfirmationType('difficulty');
      setShowResetGameConfirmation(true);

      // Store the selected difficulty to use when confirmed
      localStorage.setItem('pendingDifficultyChange', newDifficulty.toString());
      return false;
    } else {
      // No progress to lose, apply the difficulty change immediately
      setDifficulty(newDifficulty);
      saveUserPreferredDifficulty(newDifficulty);
      const newSeed = getDateSeed(currentPuzzleDate, newDifficulty);
      window.location.href = `/sudoku/${newSeed}`;
      return true;
    }
  };

  // Update the handleConfirmResetGame function to update difficulty preference
  const handleConfirmResetGame = () => {
    setShowResetGameConfirmation(false);

    // Check if we have a pending difficulty change
    const pendingDifficulty = localStorage.getItem('pendingDifficultyChange');

    if (pendingDifficulty) {
      // Save the new difficulty preference
      const newDifficultyValue = parseInt(pendingDifficulty);
      saveUserPreferredDifficulty(newDifficultyValue);

      // Apply new difficulty
      const newSeed = getDateSeed(currentPuzzleDate, newDifficultyValue);
      localStorage.removeItem('pendingDifficultyChange');
      window.location.href = `/sudoku/${newSeed}`;
    } else {
      // Just reset the game
      resetCurrentGame();
    }
  };

  // Count correctly placed numbers
  const getNumberCounts = useCallback(() => {
    const counts: Record<number, number> = {1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0};

    for (let i = 0; i < 9; i++) {
      for (let j = 0; j < 9; j++) {
        const value = grid[i][j];
        // Only count numbers that match the solution
        if (value >= 1 && value <= 9 && value === solution[i][j]) {
          counts[value]++;
        }
      }
    }

    return counts;
  }, [grid, solution]);

  // Calculate number counts whenever the grid or solution changes
  const numberCounts = useMemo(() => getNumberCounts(), [getNumberCounts]);

  // Toggle pause state and manage timer
  const handlePauseToggle = () => {
    const newState = !isPaused;
    setIsPaused(newState);

    // Handle timer
    if (newState) { // Pausing
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    } else if (!isPuzzleCompleted) { // Unpausing
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = setInterval(createTimerTick(setElapsedTime), 1000);
    }
  };

  const gameActionsRef = useRef({
    handleGameComplete,
    handleResetGameClick,
    handlePauseToggle,
    handleDifficultyChange,
    resetCurrentGame,
    handleConfirmResetGame
  });
  gameActionsRef.current = {
    handleGameComplete,
    handleResetGameClick,
    handlePauseToggle,
    handleDifficultyChange,
    resetCurrentGame,
    handleConfirmResetGame
  };

  // Add state for tracking if all games are completed
  const [incompleteGames, setIncompleteGames] = useState<DailyPuzzleGame[]>([
    '15puzzle',
    '2048'
  ]);

  // Check for all games completion when showing completion dialog
  useEffect(() => {
    if (showCompletionDialog) {
      const checkAllGames = async () => {
        const games = await getIncompleteDailyGames(currentPuzzleDate, 'sudoku', currentUser);
        setIncompleteGames(games);
      };

      checkAllGames();
    }
  }, [showCompletionDialog, currentPuzzleDate, currentUser]);

  // Add a function to instantly win the game
  const handleWin = useCallback(() => {
    // Set the grid to solution state
    setGrid([...solution.map(row => [...row])]);
    localStorage.setItem('sudokuGrid', JSON.stringify(solution));

    // Trigger game complete
    gameActionsRef.current.handleGameComplete();
  }, [solution]);

  // Update the keyboard event handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't handle keyboard shortcuts when a dialog is open
      if (hasOpenDialog) return;

      // Space key for pause/unpause
      if (event.key === ' ' && !isPuzzleCompleted) {
        event.preventDefault();
        gameActionsRef.current.handlePauseToggle();
      }

      // Ctrl+R for reset
      if (event.ctrlKey && event.key === 'r') {
        event.preventDefault();
        gameActionsRef.current.handleResetGameClick();
      }

      // Ctrl+D for difficulty change
      if (event.ctrlKey && event.key === 'd') {
        event.preventDefault();
        setIsDialogOpen(previous => !previous);
      }

      // K for toggling keyboard controls visibility
      if (event.key === 'k' || event.key === 'K') {
        event.preventDefault();
        setShowKeyboardControls(prev => !prev);
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPuzzleCompleted, hasOpenDialog]);

  // Add effect to save highlight number changes
  useEffect(() => {
    if (highlightNumber !== null) {
      localStorage.setItem('sudokuHighlightNumber', highlightNumber.toString());
    } else {
      localStorage.removeItem('sudokuHighlightNumber');
    }
  }, [highlightNumber]);

  const handleToggleDifficultyDialog = useCallback(() => {
    setIsDialogOpen(previous => !previous);
  }, []);

  const handleCloseDifficultyDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleCancelResetDialog = useCallback(() => {
    setShowResetGameConfirmation(false);
  }, []);

  const handleCloseCompletionDialog = useCallback(() => {
    setShowCompletionDialog(false);
  }, []);

  const handleTryAgain = useCallback(() => {
    setShowCompletionDialog(false);
    resetGame(true);
  }, [resetGame]);

  const handleTryToday = useCallback(() => {
    setShowCompletionDialog(false);
    window.location.href = '/sudoku';
  }, []);

  const handleTryFifteenPuzzle = useCallback(() => {
    setShowCompletionDialog(false);
    window.location.href = `/15puzzle/${currentSeed}`;
  }, [currentSeed]);

  const handleTry2048 = useCallback(() => {
    setShowCompletionDialog(false);
    window.location.href = `/2048/${currentSeed}`;
  }, [currentSeed]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:flex-1">
          <SudokuGameHeader
            puzzleDate={currentPuzzleDate}
            difficulty={difficulty}
            elapsedTime={elapsedTime}
            isPaused={isPaused}
            isPuzzleCompleted={isPuzzleCompleted}
            isPreviewMode={isPreviewMode}
            isDifficultyOpen={isDialogOpen}
            onToggleDifficulty={handleToggleDifficultyDialog}
            onCloseDifficulty={handleCloseDifficultyDialog}
            onDifficultyChange={handleDifficultyChange}
            onPauseToggle={handlePauseToggle}
            onReset={handleResetGameClick}
            onWin={handleWin}
          />

          <SudokuBoard
            ref={boardRef}
            grid={grid}
            originalGrid={originalGrid}
            solution={solution}
            onCellChange={handleCellChange}
            pencilMarks={pencilMarks}
            onPencilMarksChange={handlePencilMarksChange}
            onHint={handleHint}
            hintedCell={hintedCell}
            onResetGame={resetGame}
            onCoverageUsed={handleCoverageUsed}
            onAutoPencilUsed={handleAutoPencilUsed}
            onPencilError={handlePencilError}
            numberCounts={numberCounts}
            isPaused={isPaused}
            isComplete={isPuzzleCompleted}
            onSectionComplete={playStepCompleteSound}
            playTurnOnSound={playTurnOnSound}
            playTurnOffSound={playTurnOffSound}
            playWritePencilSound={playWritePencilSound}
            isDialogOpen={hasOpenDialog}
            highlightNumber={highlightNumber}
            onHighlightNumberChange={setHighlightNumber}
          />
        </div>

        <SudokuInstructions
          showKeyboardControls={showKeyboardControls}
          setShowKeyboardControls={setShowKeyboardControls}
        />
      </div>

      <SudokuResetConfirmationDialog
        isOpen={showResetGameConfirmation}
        confirmationType={confirmationType}
        onCancel={handleCancelResetDialog}
        onConfirm={handleConfirmResetGame}
      />

      <SudokuCompletionDialog
        isOpen={showCompletionDialog}
        puzzleDate={currentPuzzleDate}
        elapsedTime={elapsedTime}
        stats={gameStats}
        difficulty={difficulty}
        allGamesCompleted={incompleteGames.length === 0}
        incompleteGames={incompleteGames}
        onClose={handleCloseCompletionDialog}
        onTryAgain={handleTryAgain}
        onTryToday={handleTryToday}
        onTryFifteenPuzzle={handleTryFifteenPuzzle}
        onTry2048={handleTry2048}
      />
    </div>
  );
}
